import { useCallback, useEffect, useMemo } from "react";

import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import { useTheme } from "@mui/material/styles";

import { useAppDispatch, useAppSelector } from "app/hooks";
import Dialog from "components/dialog";

import {
  RECORDING_STATE_IN_PROGRESS,
  RECORDING_STATE_SAVING,
  getScribeModalNestedDialogZIndex
} from "../medical-scribe-constants";
import {
  pauseScribeRecording,
  selectIsConfirmationDialogOpen,
  selectMedicalScribeRecordingState,
  setOpenConfirmationDialog
} from "../medical-scribe.slice";

interface MedicalScribeConfirmationDialogProps {
  handlePauseRecording: (options?: { silent?: boolean }) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

const dialogContentProps = {
  sx: {
    display: "flex",
    alignItems: "center",
    pb: "20px",
    flexDirection: "column"
  }
};

export default function MedicalScribeConfirmationDialog({
  onConfirm,
  onCancel,
  handlePauseRecording
}: MedicalScribeConfirmationDialogProps) {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const showConfirmationDialog = useAppSelector(selectIsConfirmationDialogOpen);
  const recordingState = useAppSelector(selectMedicalScribeRecordingState);

  const handleChange = useCallback(() => {
    // Close dialog first
    dispatch(setOpenConfirmationDialog(false));
    // Then trigger stopRecording which will set saving state
    // Use setTimeout to ensure dialog closes before stopRecording updates state
    setTimeout(() => {
      onConfirm();
    }, 0);
  }, [dispatch, onConfirm]);

  const handleCancel = useCallback(() => {
    dispatch(setOpenConfirmationDialog(false));
    // Recover if Stop was opened while already paused (legacy path left SAVING)
    if (recordingState === RECORDING_STATE_SAVING) {
      dispatch(pauseScribeRecording());
    }
    onCancel();
  }, [dispatch, onCancel, recordingState]);

  const sx = useMemo(
    () => ({
      zIndex: getScribeModalNestedDialogZIndex(theme.zIndex.snackbar)
    }),
    [theme.zIndex.snackbar]
  );

  useEffect(() => {
    if (!showConfirmationDialog) {
      return;
    }

    // Already paused (e.g. user clicked Pause, then Stop): keep Paused UI so they
    // can still read the dialog and confirm. Do not force SAVING — that traps the
    // widget on "Please Wait…" while pauseRecording is a no-op.
    if (recordingState !== RECORDING_STATE_IN_PROGRESS) {
      return;
    }

    // Pause without SAVING so Cancel → resume is not blocked by isPausing + Please Wait.
    void handlePauseRecording({ silent: true });
  }, [
    handlePauseRecording,
    showConfirmationDialog,
    recordingState,
    dispatch
  ]);

  return (
    <Dialog
      title="Are you sure you want to stop audio recording?"
      onClose={handleCancel}
      open={showConfirmationDialog}
      dialogContentProps={dialogContentProps}
      sx={sx}
    >
      <DialogActions>
        <Button
          data-testid="scribe-confirm-dialog-cancel"
          variant="outlined"
          size="small"
          onClick={handleCancel}
        >
          Cancel
        </Button>

        <Button
          data-testid="scribe-confirm-dialog-yes"
          variant="contained"
          size="small"
          onClick={handleChange}
        >
          Yes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
